import { concat, path, pathOr, prop, propEq, propOr, reject, replace } from 'ramda';
import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import { datePathTsToFr } from '@@viewHelpers/date';
import { formatPhone } from '@@viewHelpers/phone';
import { civilityOptions, getLabelFromValue } from '@@viewCommonComponents/forms/options';

const useMembersInformation = ({
  regimeObligatoire,
  professionalSituations,
  jobs,
  socioProfessionalCategories,
  activities,
  customer,
  getMiddleText,
  getMiddleBoolean
}) => {
  const extracRegimeEnum = replace('REGIME_PRO_', '');

  const customerCardOptions = [
    {
      displayed:
        getMiddleBoolean('ADHERENT_CIVILITE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_CIVILITE_LABEL'),
      value: getLabelFromValue(prop('civility', customer), civilityOptions)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_NOM_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_NOM_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_NOM_LABEL'),
      value: propOr('', 'lastName', customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_PRENOM_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_PRENOM_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_PRENOM_LABEL'),
      value: propOr('', 'firstName', customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_DATE_NAISSANCE_LABEL'),
      value: datePathTsToFr('birthDate', customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP1'),
      value: pathOr('', ['addresses', 0, 'fields', 0], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP2'),
      value: pathOr('', ['addresses', 0, 'fields', 1], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP3'),
      value: pathOr('', ['addresses', 0, 'fields', 2], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_CODE_POSTAL_LABEL'),
      value: pathOr('', ['addresses', 0, 'zipCode'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_VILLE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_VILLE_LABEL'),
      value: pathOr('', ['addresses', 0, 'city'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_TEL_MOBILE_LABEL'),
      value: formatPhone(propOr([], 'phones', customer), true)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_TEL_FIXE_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_TEL_FIXE_LABEL'),
      value: formatPhone(propOr([], 'phones', customer), false)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_EMAIL_LABEL'),
      value: pathOr([], ['emails', 0, 'value'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_NSS_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_NSS_LABEL'),
      value: concat(
        pathOr('', ['socialSecurities', 0, 'number'], customer),
        pathOr('', ['socialSecurities', 0, 'key'], customer)
      )
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_REGIME_LABEL'),
      value: getLabelFromValue(
        pathOr('-', ['socialSecurities', 0, 'regime'], customer),
        propOr([], 'regimes', regimeObligatoire)
      )
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_RANG_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_RANG_LABEL'),
      value: pathOr('', ['socialSecurities', 0, 'twinsRank'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_SIT_PRO_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_SIT_PRO_LABEL'),
      value: getLabelFromValue(
        pathOr('', ['CSPs', 0, 'categoryLvl1CSPExternalId'], customer),
        professionalSituations
      )
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CSP_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_CSP_LABEL'),
      value: getLabelFromValue(
        pathOr('', ['CSPs', 0, 'categoryCSPExternalId'], customer),
        socioProfessionalCategories
      )
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_PROFESSION_LABEL'),
      value: getLabelFromValue(path(['CSPs', 0, 'externalId'], customer), jobs)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_CAISSE_PRO_LABEL'),
      value: extracRegimeEnum(pathOr('', ['socialSecurities', 0, 'regime'], customer))
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_REVENU_ANNUEL_LABEL'),
      value: concat(pathOr('-', ['finances', 0, 'annualIncome'], customer), ' €')
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ACTIVITE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_ACTIVITE_LABEL'),
      value: getLabelFromValue(propOr('', 'professionalStatus', customer), activities)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ID_TNS_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT'),
      label: 'Travailleur Non Salarié',
      value: prop('isTNS', customer) ? 'Oui' : 'Non'
    }
  ];

  const form = useFormContext();
  const {
    formState: { errors }
  } = form;

  const datePickerElem = document.getElementById('datePicker');

  useEffect(() => {
    if (isNotNilOrEmpty(prop('effectDate', errors))) {
      datePickerElem.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  }, [errors]);

  return {
    customerCardOptions: reject(propEq('displayed', false), customerCardOptions),
    form,
    datePickerElem
  };
};

export default useMembersInformation;
